import { useState } from 'react'
import { v4 } from 'uuid'
import { useTranslation } from 'react-i18next'

import CameraIcon from '@assets/images/comment-camera-icon.svg?react'
import SendIcon from '@assets/images/comment-send-icon.svg?react'
import DeleteFileIcon from '@assets/images/comment-delete-file-icon.svg?react'

import { LocalFile } from '@types'

import { mapFileToLocalFile } from '@utils/mappers'
import { useIOSPreventAutoZooming } from '@utils/hooks/useIOSPreventZooming'

import { UserBadge, UserBadgeSize } from '@components/UserBadge'

import { Loader } from '@components/Loader'

import { useMe } from '@hooks/user/me.hook'

import {
  Container,
  TextAreaContainer,
  TextArea,
  HeaderContainer,
  LabelTitle,
  SendButtonContainer,
  MainContainer,
  CameraButtonContainer,
  CameraButtonInput,
  FilePreview,
  FileContainter,
  DeleteFileContainer,
  FilesPreviewContainter,
  LoaderContainer,
} from './WriteComment.styled'

export interface SendProps {
  value: string
  files: LocalFile[]
  reset: () => void
  setLoading: (loading: boolean) => void
}

interface Props {
  className?: string
  autoFocus?: boolean
  onClickSend: (props: SendProps) => void
  userBadgeSize?: UserBadgeSize
  userBadgeHidden?: boolean
  placeholder?: string
  disabled?: boolean
}

const AUTHORIZED_FILE_EXTENSIONS = 'image/jpeg, image/png, image/jpg'

export const WriteComment = (props: Props): JSX.Element => {
  const {
    className,
    autoFocus,
    userBadgeSize = 'medium',
    userBadgeHidden = false,
    placeholder,
    disabled,
    onClickSend,
  } = props

  const { t } = useTranslation()

  const [loading, setLoading] = useState<boolean>(false)

  const [height, setHeight] = useState<number>(28)
  const [value, setValue] = useState<string>('')

  const [files, setFiles] = useState<LocalFile[]>([])
  const selectedFile = files[0]

  const reset = () => {
    setValue('')
    setFiles([])
    setLoading(false)
  }

  useIOSPreventAutoZooming()

  return (
    <Container
      className={className}
      style={{ maxHeight: selectedFile ? 258 : 196, minHeight: userBadgeHidden ? 54 : 120 }}
    >
      {!userBadgeHidden && (
        <HeaderContainer>
          <MeUserBadge userBadgeSize={userBadgeSize} />
          <LabelTitle>{t('components_WriteComment_label_add_comment')}</LabelTitle>
        </HeaderContainer>
      )}

      {selectedFile && (
        <FilesPreviewContainter>
          <FileContainter>
            <FilePreview
              src={selectedFile.url}
              onClick={() => {
                if (loading) return

                window.open(selectedFile.url, '_blank')
              }}
            />
            {!loading && (
              <DeleteFileContainer onClick={() => setFiles([])}>
                <DeleteFileIcon />
              </DeleteFileContainer>
            )}
          </FileContainter>
        </FilesPreviewContainter>
      )}
      <MainContainer>
        <TextAreaContainer>
          <TextArea
            style={{ height }}
            disabled={loading || disabled}
            placeholder={placeholder || t('components_WriteComment_placeholder_add_comment')}
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
            }}
            onBlur={() => {
              setHeight(28)
            }}
            onInput={(e) => {
              setHeight(e.currentTarget.scrollHeight)
            }}
            onPaste={(e) => {
              setHeight(e.currentTarget.scrollHeight)
            }}
            autoFocus={autoFocus}
            onKeyDown={(e) => {
              if (!loading && e.metaKey && e.key == 'Enter') {
                onClickSend({
                  value,
                  files,
                  reset,
                  setLoading,
                })

                e.currentTarget.blur()
              }
            }}
          />
        </TextAreaContainer>

        <CameraButtonContainer>
          <CameraIcon />

          <CameraButtonInput
            key={v4()}
            type="file"
            disabled={loading || disabled}
            accept={AUTHORIZED_FILE_EXTENSIONS}
            onChange={(e) => {
              const files = [...(e.target.files || [])].map((_file) => mapFileToLocalFile(_file)) as LocalFile[]
              setFiles(files)
            }}
          />
        </CameraButtonContainer>

        <SendButtonContainer
          disabled={(!value && !selectedFile) || disabled}
          onClick={() => {
            if ((!value && !selectedFile) || loading) return

            onClickSend({
              value,
              files,
              reset,
              setLoading,
            })
          }}
        >
          {loading ? (
            <LoaderContainer>
              <Loader size={14} />
            </LoaderContainer>
          ) : (
            <SendIcon />
          )}
        </SendButtonContainer>
      </MainContainer>
    </Container>
  )
}

const MeUserBadge = ({ userBadgeSize }: { userBadgeSize: UserBadgeSize }): JSX.Element => {
  const { getMe } = useMe()

  return <UserBadge user={getMe()} size={userBadgeSize} />
}
